import React, { useState } from 'react';
import { X, User as UserIcon, Mail, Phone, Calendar, MessageSquare, Pencil as Edit, CheckCircle, Play, Volume2, Download, Plus, Tag, Send, Clock, FileText, Video, MessageCircle, PhoneCall, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';

interface LeadDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: any;
  onEdit: () => void;
  onUpdateLead?: (leadId: string, updates: any) => void;
}

/* ================= UTILITIES ================ */
const normalizePgTs = (v: any): Date | null => {
  if (!v) return null;
  if (typeof v !== 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  const raw = v.trim();
  if (!/\d{4}-\d{2}-\d{2}/.test(raw)) return null;
  let s = raw.includes('T') ? raw : raw.replace(' ', 'T');
  if (!/[Z]|[+\-]\d\d(?::?\d\d)?$/.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (dateValue: any): string => {
  const d = normalizePgTs(dateValue);
  return d ? format(d, 'dd MMMM yyyy HH:mm', { locale: tr }) : 'Belirtilmemiş';
};
const formatDate = (dateValue: any): string => {
  const d = normalizePgTs(dateValue);
  return d ? format(d, 'dd MMMM yyyy', { locale: tr }) : 'Belirtilmemiş';
};
const formatTimeHM = (timeValue: any): string => {
  if (!timeValue) return 'Belirtilmemiş';
  const s = String(timeValue);
  const m = s.match(/^(\d{2}:\d{2})/);
  return m ? m[1] : s;
};
const yesNoText = (value: any): string => {
  if (value === true) return 'Evet';
  if (value === false) return 'Hayır';
  return 'Belirtilmemiş';
};

/* Güvenli getter'lar – JSON string'leri de parse eder */
const getValue = (obj: any, key: string, fallback: string = 'Belirtilmemiş'): any => {
  if (!obj || typeof obj !== 'object') return fallback;
  const value = obj[key];
  if (value === null || value === undefined || value === '') return fallback;
  return value;
};
const getArrayValue = (obj: any, key: string): string[] => {
  if (!obj || typeof obj !== 'object') return [];
  const value = obj[key];
  if (Array.isArray(value)) return value.filter((i) => i !== null && i !== undefined);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};
const getBooleanValue = (obj: any, key: string): boolean | null => {
  if (!obj || typeof obj !== 'object') return null;
  const value = obj[key];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  return null;
};
const getObjectValue = (obj: any, key: string): any => {
  if (!obj || typeof obj !== 'object') return {};
  const value = obj[key];
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

/* ================= MAPPINGS ================ */
const getStatusText = (status: string): string => {
  const map: Record<string, string> = {
    lead: 'Potansiyel',
    contacted: 'İletişim Kuruldu',
    interested: 'İlgili',
    applied: 'Başvuru Yapıldı',
    accepted: 'Kabul Edildi',
    rejected: 'Reddedildi',
    enrolled: 'Kayıt Oldu',
  };
  return map[status] || status || 'Belirtilmemiş';
};
const getStatusColor = (status: string): string => {
  const map: Record<string, string> = {
    lead: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    contacted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    interested: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    applied: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
    accepted: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    enrolled: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400',
  };
  return map[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
};
const getPriorityText = (priority: string): string => {
  const map: Record<string, string> = { high: 'Yüksek', medium: 'Orta', low: 'Düşük' };
  return map[priority] || 'Belirtilmemiş';
};
const getSourceText = (source: string): string => {
  const map: Record<string, string> = {
    website: 'Website',
    referral: 'Referans',
    'social-media': 'Sosyal Medya',
    advertisement: 'Reklam',
    'walk-in': 'Ziyaret',
    other: 'Diğer',
  };
  return map[source] || source || 'Belirtilmemiş';
};
const getRelationshipText = (relationship: string): string => {
  const map: Record<string, string> = {
    anne: 'Anne',
    baba: 'Baba',
    kardes: 'Kardeş',
    diger: 'Diğer',
    mother: 'Anne',
    father: 'Baba',
    sibling: 'Kardeş',
    other: 'Diğer',
  };
  return map[relationship] || relationship || 'Belirtilmemiş';
};
const getMeetingTypeText = (type: string): string => {
  const map: Record<string, string> = {
    phone: 'Telefonda Görüşme',
    video: 'Online Görüşme',
    'in-person': 'Fiziki Görüşme',
    whatsapp: 'WhatsApp Mesajlaşma',
    email: 'E-mail',
  };
  return map[type] || type || 'Belirtilmemiş';
};
const getInterestedServiceText = (service: string): string => {
  const map: Record<string, string> = {
    education: 'Sadece Eğitim',
    visa: 'Sadece Vize',
    both: 'Eğitim + Vize',
    other: 'Diğer',
  };
  return map[service] || service || 'Belirtilmemiş';
};
const getFollowUpStatusText = (status: string): string => {
  const map: Record<string, string> = {
    pending: 'Beklemede',
    scheduled: 'Planlandı',
    completed: 'Tamamlandı',
    'no-response': 'Cevap Yok',
  };
  return map[status] || status || 'Belirtilmemiş';
};

/* ================ SMALL UI BLOCKS ================= */
const InfoItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '');
  return (
    <div>
      <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
      <div className="font-medium text-gray-900 dark:text-white">
        {empty ? 'Belirtilmemiş' : <>{value}</>}
      </div>
    </div>
  );
};

const EmailsItem: React.FC<{ label: string; emails: string[] }> = ({ label, emails }) => {
  const validEmails = emails.filter(e => e && e.trim());
  return (
    <div>
      <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
      <div className="font-medium text-gray-900 dark:text-white">
        {validEmails.length === 0 ? (
          'Belirtilmemiş'
        ) : (
          <div className="space-y-1">
            {validEmails.map((email, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Mail className="w-3 h-3 text-gray-400" />
                <span>{email}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusItem: React.FC<{ label: string; status: any; onClick?: () => void; clickable?: boolean }> = ({ label, status, onClick, clickable = false }) => (
  <div
    className={`flex items-center space-x-2 ${clickable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors' : ''}`}
    onClick={clickable ? onClick : undefined}
  >
    <CheckCircle className={`w-5 h-5 ${status ? 'text-green-500' : 'text-gray-400'}`} />
    <span className={`text-sm ${status ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
      {label}
    </span>
  </div>
);

const PillList: React.FC<{ title: string; items: string[]; color: 'blue' | 'green' | 'purple' | 'orange' }> = ({
  title,
  items,
  color,
}) => {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    green: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
  };
  return (
    <div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{title}</p>
      {items.length === 0 ? (
        <p className="text-gray-700 dark:text-gray-300">Belirtilmemiş</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((item, i) => (
            <span key={`${item}-${i}`} className={`px-3 py-1 ${colorClasses[color]} text-sm rounded-full`}>
              {item}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const ScoreItem: React.FC<{ label: string; value: number; max: number; color: string }> = ({
  label,
  value,
  max,
  color,
}) => {
  const percentage = Math.min((value / max) * 100, 100);
  const colorClasses: Record<string, string> = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500',
    yellow: 'text-yellow-500',
    orange: 'text-orange-500',
    red: 'text-red-500',
  };
  return (
    <div className="text-center">
      <div className="relative w-16 h-16 mx-auto mb-2">
        <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray={`${percentage}, 100`}
            className={colorClasses[color] || 'text-gray-500'}
          />
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="0, 100"
            className="text-gray-200 dark:text-gray-700"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold text-gray-900 dark:text-white">{value}</span>
        </div>
      </div>
      <p className="text-xs font-medium text-gray-900 dark:text-white">{label}</p>
      <p className="text-xs text-gray-500 dark:text-gray-500">/{max}</p>
    </div>
  );
};

/* ================== COMPONENT ================== */
const LeadDetailModal: React.FC<LeadDetailModalProps> = ({ isOpen, onClose, lead, onEdit, onUpdateLead }) => {
  const { user } = useAuth();
  const [relatedLeads, setRelatedLeads] = useState<any[]>([]);
  const [studentInfo, setStudentInfo] = useState<any>(null);
  const [interactions, setInteractions] = useState<any[]>(() => {
    // Lead'den mevcut etkileşimleri yükle
    try {
      return lead?.interactions ? JSON.parse(lead.interactions) : [];
    } catch {
      return [];
    }
  });
  const [newInteraction, setNewInteraction] = useState({
    type: 'call' as 'call' | 'email' | 'meeting' | 'whatsapp' | 'other',
    description: '',
    notes: '',
    outcome: 'positive' as 'positive' | 'neutral' | 'negative',
    followUpRequired: false,
    followUpDate: '',
    followUpNotes: ''
  });
  const [activities, setActivities] = useState<any[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [callNotes, setCallNotes] = useState<any[]>([]);
  const [callNotesLoading, setCallNotesLoading] = useState(false);
  const [existingContract, setExistingContract] = useState<any>(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [contractNote, setContractNote] = useState('');
  const [isUploadingContract, setIsUploadingContract] = useState(false);

  // Reminders Effect
  // (Moved to EditLeadModal)

  // Kayıt Oldu butonuna tıklandığında
  const handleEnrollmentToggle = async () => {
    if (!lead || !onUpdateLead) return;

    const newRegisteredStatus = !getBooleanValue(lead, 'is_registered');

    try {
      const updates = {
        is_registered: newRegisteredStatus,
        status: newRegisteredStatus ? 'enrolled' : lead.status,
        tracking_status: newRegisteredStatus ? 'enrolled' : 'new_lead',
        updated_at: new Date().toISOString()
      };

      await onUpdateLead(lead.id, updates);

      // Eğer kayıt oldu durumuna çekiliyorsa ve daha önce proposal yoksa, otomatik proposal oluştur
      if (newRegisteredStatus && lead.status !== 'enrolled') {
        const { supabase } = await import('../../lib/supabase');

        // Önce bu lead için zaten bir proposal var mı kontrol et
        const { data: existingProposal } = await supabase
          .from('proposals')
          .select('id')
          .eq('lead_id', lead.id)
          .maybeSingle();

        // Eğer yoksa yeni anlaşma kaydı oluştur
        if (!existingProposal) {
          const cleanEmails = getArrayValue(lead, 'emails');

          // Fiyat bilgisini al
          const discussedPrice = getValue(lead, 'discussed_price', '0');
          const priceValue = parseFloat(discussedPrice.replace(/[^0-9.-]/g, '')) || 0;

          // Telefon numarasını al
          const phoneNumber = getValue(lead, 'phone') ||
            getObjectValue(lead, 'student_info').phone ||
            'Belirtilmedi';

          // valid_until için 30 gün sonrası
          const validUntil = new Date();
          validUntil.setDate(validUntil.getDate() + 30);

          const proposalData = {
            lead_id: lead.id,
            student_name: `${getValue(lead, 'first_name', '')} ${getValue(lead, 'last_name', '')}`,
            student_email: cleanEmails[0] || 'Belirtilmedi',
            university: 'Kayıt Oldu',
            program: 'Kayıt Oldu',
            degree: 'Kayıt Oldu',
            total_amount: priceValue,
            service_fee: priceValue,
            currency: 'EUR',
            status: 'accepted',
            created_by: lead.created_by || user?.id,
            consultant_name: user?.name || 'Bilinmiyor',
            valid_until: validUntil.toISOString(),
            notes: `Öğrenci: ${getValue(lead, 'first_name', '')} ${getValue(lead, 'last_name', '')}\nTelefon: ${phoneNumber}\nEmail: ${cleanEmails[0] || 'Belirtilmedi'}\nFiyat: ${discussedPrice} EUR\n\n${getValue(lead, 'notes', '')}`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { error: proposalError } = await supabase
            .from('proposals')
            .insert(proposalData);

          if (proposalError) {
            console.error('Anlaşma oluşturma hatası:', proposalError);
            alert('Anlaşma oluşturulurken bir hata oluştu: ' + proposalError.message);
          } else {
            alert('Lead kayıt oldu durumuna güncellendi ve anlaşmalar sayfasına eklendi!');
          }
        } else {
          alert('Lead kayıt oldu durumuna güncellendi!');
        }
      } else if (!newRegisteredStatus) {
        alert('Lead kayıt durumu iptal edildi.');
      }

      // Lead'i yeniden yükle
      window.location.reload();
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      alert('Güncelleme sırasında bir hata oluştu.');
    }
  };

  // Aktiviteleri yükle
  React.useEffect(() => {
    if (!lead?.id || !isOpen) return;

    const loadActivities = async () => {
      setActivitiesLoading(true);
      try {
        const { supabase } = await import('../../lib/supabase');
        const { data, error } = await supabase
          .from('lead_activities')
          .select(`
            *,
            user:user_id(id, name, email)
          `)
          .eq('lead_id', lead.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setActivities(data || []);
      } catch (err) {
        console.error('Error loading activities:', err);
      } finally {
        setActivitiesLoading(false);
      }
    };

    loadActivities();
  }, [lead?.id, isOpen]);

  // Öğrenci ve ilgili lead'leri yükle
  React.useEffect(() => {
    if (!lead?.student_id) return;

    const fetchRelatedLeads = async () => {
      try {
        const { supabase } = await import('../../lib/supabase');

        // Öğrenci bilgilerini al
        const { data: student } = await supabase
          .from('students')
          .select('*')
          .eq('id', lead.student_id)
          .maybeSingle();

        setStudentInfo(student);

        // Aynı öğrenciye ait diğer lead'leri al
        const { data: leads } = await supabase
          .from('leads')
          .select(`
            *,
            student_parents!inner(relation_type, is_primary_contact)
          `)
          .eq('student_id', lead.student_id)
          .neq('id', lead.id);

        setRelatedLeads(leads || []);
      } catch (error) {
        console.error('İlişkili lead\'ler yüklenirken hata:', error);
      }
    };

    fetchRelatedLeads();
  }, [lead?.student_id]);

  // Görüşme notlarını yükle
  React.useEffect(() => {
    if (!lead?.id) return;

    const fetchCallNotes = async () => {
      setCallNotesLoading(true);
      try {
        const { supabase } = await import('../../lib/supabase');
        const { data, error } = await supabase
          .from('call_notes')
          .select(`
            *,
            profiles:created_by(id, name, email)
          `)
          .eq('lead_id', lead.id)
          .order('call_date', { ascending: false });

        if (error) throw error;
        setCallNotes(data || []);
      } catch (error) {
        console.error('Görüşme notları yüklenirken hata:', error);
      } finally {
        setCallNotesLoading(false);
      }
    };

    fetchCallNotes();
    fetchCallNotes();
  }, [lead?.id]);

  // Sözleşme bilgisini yükle
  React.useEffect(() => {
    if (!lead?.student_id) return;

    const fetchContract = async () => {
      setContractLoading(true);
      try {
        const { supabase } = await import('../../lib/supabase');
        const { data, error } = await supabase
          .from('contracts')
          .select('*')
          .eq('student_id', lead.student_id)
          .maybeSingle();

        if (error) throw error;
        setExistingContract(data);
      } catch (error) {
        console.error('Sözleşme bilgisi yüklenirken hata:', error);
      } finally {
        setContractLoading(false);
      }
    };

    fetchContract();
    fetchContract();
  }, [lead?.student_id]);

  if (!isOpen || !lead) return null;

  // --- Extracts ---
  const contactRole: string = (getValue(lead, 'contact_role', 'student') as string) || 'student';
  const studentInfoFromLead = getObjectValue(lead, 'student_info');
  const guardianInfo = getObjectValue(lead, 'guardian_info');

  const tags = getArrayValue(lead, 'tags');
  const interestedCountries = getArrayValue(lead, 'interested_countries');
  const interestedPrograms = getArrayValue(lead, 'interested_programs');
  const interestedUniversities = getArrayValue(lead, 'interested_universities');
  const assignedConsultants = getArrayValue(lead, 'assigned_consultants');

  const voiceNotes = (() => {
    const raw = lead?.voice_notes;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  })();

  // --- Interactions helpers ---
  const addInteraction = async () => {
    if (!newInteraction.description.trim()) return;

    try {
      const { supabase } = await import('../../lib/supabase');

      // Aktiviteyi veritabanına kaydet
      const { data: activityData, error } = await supabase
        .from('lead_activities')
        .insert({
          lead_id: lead.id,
          user_id: user?.id,
          activity_type: newInteraction.type,
          description: newInteraction.description,
          notes: newInteraction.notes,
          outcome: newInteraction.outcome,
          follow_up_required: newInteraction.followUpRequired,
          follow_up_date: newInteraction.followUpDate || null,
          follow_up_notes: newInteraction.followUpNotes || null
        })
        .select(`
          *,
          user:user_id(id, name, email)
        `)
        .single();

      if (error) throw error;

      // Aktivite listesini güncelle
      setActivities((prev) => [activityData, ...prev]);

      // Lead'in last_contact bilgisini güncelle
      if (onUpdateLead) {
        await onUpdateLead(lead.id, {
          last_contact: new Date().toISOString()
        });
      }

      setNewInteraction({
        type: 'call',
        description: '',
        notes: '',
        outcome: 'positive',
        followUpRequired: false,
        followUpDate: '',
        followUpNotes: ''
      });

      alert('Etkileşim başarıyla kaydedildi!');
    } catch (error) {
      console.error('Etkileşim kaydedilirken hata:', error);
      alert('Etkileşim kaydedilirken hata oluştu.');
    }
  };

  const getInteractionIcon = (type: string) => {
    switch (type) {
      case 'call': return Phone;
      case 'email': return Mail;
      case 'meeting': return Calendar;
      case 'whatsapp': return MessageSquare;
      case 'created': return Plus;
      case 'updated': return Edit;
      case 'status_changed': return CheckCircle;
      default: return MessageSquare;
    }
  };
  const getInteractionColor = (type: string) => {
    switch (type) {
      case 'call': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400';
      case 'email': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'meeting': return 'text-purple-600 bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400';
      case 'whatsapp': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'created': return 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400';
      case 'updated': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400';
      case 'status_changed': return 'text-cyan-600 bg-cyan-100 dark:bg-cyan-900/20 dark:text-cyan-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getOutcomeColor = (outcome: string) => {
    switch (outcome) {
      case 'positive': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'negative': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      default: return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
    }
  };


  const getCallTypeColor = (type: string) => {
    const map: Record<string, string> = {
      inbound: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      outbound: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      missed: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
    };
    return map[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const formatDuration = (seconds: number) => {
    if (seconds === 0) return '-';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getOutcomeText = (outcome: string) => {
    switch (outcome) {
      case 'positive': return 'Olumlu';
      case 'negative': return 'Olumsuz';
      default: return 'Nötr';
    }
  };

  const handleEmailClick = (email: string) => {
    if (!email) return;

    // Seçili Gmail imzasını al
    const selectedSignatureId = localStorage.getItem('gmail_selected_signature');
    let signatureContent = '';

    if (selectedSignatureId) {
      // Gmail imzalarını localStorage'dan al (gerçek uygulamada API'den gelecek)
      try {
        const signatures = JSON.parse(localStorage.getItem('gmail_signatures') || '[]');
        const signature = signatures.find((sig: any) => sig.id === selectedSignatureId);
        if (signature) {
          signatureContent = `\n\n${signature.content}`;
        }
      } catch (e) {
        console.error('İmza yüklenirken hata:', e);
      }
    }

    // Gmail'de yeni mail oluştur
    const subject = encodeURIComponent(`Pisa Educazione - ${lead.first_name} ${lead.last_name} Takip`);
    const body = encodeURIComponent(`Merhaba ${lead.first_name},\n\nPisa Educazione'den size ulaşıyorum.${signatureContent || `\n\nSaygılarımla,\n${user?.name || 'Danışman'}`}`);

    // Gmail compose URL'si
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;

    // Yeni sekmede aç
    window.open(gmailUrl, '_blank');
  };

  const handleSendToAccounting = async () => {
    if (!contractFile) {
      alert('Lütfen önce bir sözleşme dosyası seçiniz.');
      return;
    }

    // --- JIT FETCH LOGIC START ---
    let effectiveStudentId = lead.student_id;
    if (!effectiveStudentId && (lead.status === 'enrolled' || lead.is_registered)) {
      try {
        const { supabase } = await import('../../lib/supabase');
        const { data: refreshedLead } = await supabase
          .from('leads')
          .select('student_id')
          .eq('id', lead.id)
          .single();

        if (refreshedLead?.student_id) {
          effectiveStudentId = refreshedLead.student_id;
          console.log('JIT Fetch: Student ID found:', effectiveStudentId);
        }
      } catch (error) {
        console.error('JIT Fetch Error:', error);
      }
    }
    // --- JIT FETCH LOGIC END ---

    // --- AUTO-CREATE STUDENT LOGIC START ---
    if (!effectiveStudentId) {
      // If still no student ID after JIT fetch, create one!
      try {
        const { supabase } = await import('../../lib/supabase');
        console.log('Auto-creating student for lead:', lead.id);

        // 1. Prepare Data
        // Helper to safely get student info
        const studentInfo = getObjectValue(lead, 'student_info');
        const contactRole = getValue(lead, 'contact_role', 'student');

        const studentData = {
          first_name: contactRole === 'guardian' ? (studentInfo.first_name || getValue(lead, 'first_name')) : getValue(lead, 'first_name'),
          last_name: contactRole === 'guardian' ? (studentInfo.last_name || getValue(lead, 'last_name')) : getValue(lead, 'last_name'),
          email: contactRole === 'guardian' ? (getArrayValue(studentInfo, 'emails')[0] || null) : (getArrayValue(lead, 'emails')[0] || null),
          phone: contactRole === 'guardian' ? studentInfo.phone : getValue(lead, 'phone'),
          branch_id: lead.branch_id || user?.branchId,
          nationality: getValue(lead, 'nationality'),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        // 2. Insert Student
        const { data: newStudent, error: studentError } = await supabase
          .from('students')
          .insert(studentData)
          .select()
          .single();

        if (studentError) throw studentError;

        // 3. Link Parent
        const parentData = {
          student_id: newStudent.id,
          lead_id: lead.id,
          relation_type: contactRole === 'guardian' ? (getObjectValue(lead, 'guardian_info')?.relationship || 'veli') : 'kendisi',
          is_primary_contact: true
        };

        const { error: parentError } = await supabase.from('student_parents').insert(parentData);
        if (parentError) console.error('Error linking parent:', parentError);

        // 4. Update Lead
        const { error: leadUpdateError } = await supabase
          .from('leads')
          .update({ student_id: newStudent.id })
          .eq('id', lead.id);

        if (leadUpdateError) throw leadUpdateError;

        // 5. Update Local ID for next step
        effectiveStudentId = newStudent.id;
        console.log('Auto-created student successfully:', effectiveStudentId);

        // Optional: Notify user implicitly
        // alert('Öğrenci kartı otomatik oluşturuldu, işlem devam ediyor...');

      } catch (createError: any) {
        console.error('Auto-create student error:', createError);
        alert('Otomatik öğrenci oluşturma hatası: ' + createError.message);
        return;
      }
    }
    // --- AUTO-CREATE STUDENT LOGIC END ---

    if (!await window.appConfirm(`${lead.first_name} ${lead.last_name} için sözleşmeyi ve bilgileri muhasebeye göndermek istiyor musunuz?`)) return;

    setIsUploadingContract(true);
    try {
      const { supabase } = await import('../../lib/supabase');

      // 1. Upload File
      const fileExt = contractFile.name.split('.').pop();
      const fileName = `contract-${lead.id}-${Date.now()}.${fileExt}`;
      const filePath = `contracts/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, contractFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      // 2. Prepare Data
      // Price parsing
      const discussedPrice = getValue(lead, 'discussed_price', '0');
      const priceValue = parseFloat(String(discussedPrice).replace(/[^0-9.-]/g, '')) || 0;

      // Contract Number Generation (Simple)
      const contractNumber = `CNT-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;

      // 3. Create Contract Record
      const { error: contractError } = await supabase
        .from('contracts')
        .insert({
          contract_number: contractNumber,
          student_id: effectiveStudentId,
          branch_id: user?.branchId || lead.branch_id, // Fixed: use branchId property from AuthContext
          created_by: user?.id,
          contract_date: new Date().toISOString(),
          total_amount: priceValue,
          currency: 'EUR', // Default or fetch from lead
          contract_file_url: publicUrl,
          status: 'pending', // Pending accounting review
          notes: `${contractNote ? `NOT: ${contractNote}\n\n` : ''}Lead Detay tarafından gönderildi. \nOluşturan: ${user?.name || 'Bilinmiyor'}`,
          transferred_to_operations: false
        });

      if (contractError) throw contractError;

      alert('Sözleşme başarıyla yüklendi ve muhasebeye gönderildi!');
      setContractFile(null);
      // Optional: Refresh or close

    } catch (error: any) {
      console.error('Muhasebeye gönderme hatası:', error);
      alert('İşlem sırasında hata oluştu: ' + error.message);
    } finally {
      setIsUploadingContract(false);
    }
  };

  /* ================= RENDER BLOCKS ================= */
  const renderDetails = () => (
    <div className="space-y-6">
      {/* Sözleşme & Muhasebe Kartı */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-600" />
          Sözleşme ve Muhasebe
        </h4>
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-5 border border-purple-100 dark:border-purple-800">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
            Öğrencinin sözleşmesini yükleyerek muhasebe departmanına iletebilirsiniz.
            <br />
            <span className="text-xs text-gray-500">Bu işlem, sözleşmeyi "Sözleşmeler" sayfasına ekleyecektir.</span>
          </p>

          {existingContract ? (
            <div className="w-full space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-300">
                    Sözleşme muhasebeye gönderildi.
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    Gereksiz tekrar gönderimi engellemek için işlem kilitlenmiştir.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sözleşme Dosyası
                    </span>
                  </div>
                  {existingContract.contract_file_url ? (
                    <a
                      href={existingContract.contract_file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Görüntüle
                    </a>
                  ) : (
                    <span className="text-sm text-gray-500">Dosya Yok</span>
                  )}
                </div>

                {existingContract.notes && (
                  <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 mb-1">Muhasebe Notu</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {existingContract.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="flex-1 w-full space-y-3">
                <textarea
                  value={contractNote}
                  onChange={(e) => setContractNote(e.target.value)}
                  placeholder="Muhasebe için notunuzu buraya ekleyebilirsiniz..."
                  className="w-full px-4 py-2 border border-purple-200 dark:border-purple-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 text-sm"
                  rows={2}
                />
                <label className="block w-full cursor-pointer">
                  <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed transition-colors ${contractFile ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 bg-white dark:bg-gray-800'}`}>
                    <Upload className={`w-5 h-5 ${contractFile ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${contractFile ? 'text-green-700' : 'text-gray-500'}`}>
                      {contractFile ? contractFile.name : 'Sözleşme Dosyası Seç (PDF/Resim)'}
                    </span>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setContractFile(e.target.files[0]);
                      }
                    }}
                  />
                </label>
              </div>

              <button
                onClick={handleSendToAccounting}
                disabled={!contractFile || isUploadingContract}
                className="w-full sm:w-auto px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isUploadingContract ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Muhasebeye Gönder
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Görüşme Bilgileri ve Etkileşimler */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Görüşme Bilgileri</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoItem label="Görüşme Tarihi" value={formatDate(getValue(lead, 'meeting_date'))} />
          <InfoItem label="Görüşme Saati" value={formatTimeHM(getValue(lead, 'meeting_time'))} />
          <InfoItem label="Görüşme Türü" value={getMeetingTypeText(getValue(lead, 'meeting_type'))} />
          <InfoItem label="Görüşmeyi Yapan Danışman" value={lead.created_by ? (lead?.creator?.name || getValue(lead, 'meeting_consultant')) : 'Sistemden Yüklendi'} />
        </div>
        {
          getValue(lead, 'meeting_summary') && getValue(lead, 'meeting_summary') !== 'Belirtilmemiş' && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Görüşme Özeti</p>
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <p className="text-gray-700 dark:text-gray-300">{getValue(lead, 'meeting_summary')}</p>
              </div>
            </div>
          )
        }

        {/* Etkileşimler */}
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            Etkileşimler
          </h5>

          {/* Yeni Etkileşim Ekle */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-6">
            <h6 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-4">Yeni Etkileşim Ekle</h6>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Etkileşim Türü</label>
                  <select
                    value={newInteraction.type}
                    onChange={(e) => setNewInteraction((prev) => ({ ...prev, type: e.target.value as any }))}
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="call">Telefon Görüşmesi</option>
                    <option value="email">Email Gönderildi</option>
                    <option value="meeting">Görüşme Yapıldı</option>
                    <option value="whatsapp">WhatsApp Mesajı</option>
                    <option value="other">Diğer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kısa Açıklama</label>
                  <input
                    type="text"
                    value={newInteraction.description}
                    onChange={(e) => setNewInteraction((prev) => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Örn: İlk görüşme gerçekleştirildi"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Görüşme Sonucu</label>
                  <select
                    value={newInteraction.outcome}
                    onChange={(e) => setNewInteraction((prev) => ({ ...prev, outcome: e.target.value as any }))}
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="positive">Olumlu</option>
                    <option value="neutral">Nötr</option>
                    <option value="negative">Olumsuz</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    id="followUpRequired"
                    type="checkbox"
                    checked={newInteraction.followUpRequired}
                    onChange={(e) => setNewInteraction((prev) => ({ ...prev, followUpRequired: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="followUpRequired" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                    Takip gerekli
                  </label>
                </div>
              </div>

              {newInteraction.followUpRequired && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Takip Tarihi</label>
                    <input
                      type="date"
                      value={newInteraction.followUpDate}
                      onChange={(e) => setNewInteraction((prev) => ({ ...prev, followUpDate: e.target.value }))}
                      className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Takip Notları</label>
                    <input
                      type="text"
                      value={newInteraction.followUpNotes}
                      onChange={(e) => setNewInteraction((prev) => ({ ...prev, followUpNotes: e.target.value }))}
                      className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Takip için özel notlar..."
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Detaylı Notlar</label>
                <textarea
                  value={newInteraction.notes}
                  onChange={(e) => setNewInteraction((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Etkileşim detayları..."
                />
              </div>
              <button
                type="button"
                onClick={addInteraction}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Etkileşim Ekle</span>
              </button>
            </div>
          </div>

          {/* Tüm Aktiviteler (Veritabanından) */}
          {activitiesLoading ? (
            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">Yükleniyor...</p>
            </div>
          ) : activities.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">Henüz etkileşim kaydı yok</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">Yukarıdaki formu kullanarak etkileşim ekleyin</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activities.map((activity) => {
                const ActivityIcon = getInteractionIcon(activity.activity_type);
                const activityDate = normalizePgTs(activity.created_at);

                return (
                  <div key={activity.id} className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getInteractionColor(activity.activity_type)}`}>
                        <ActivityIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h5 className="font-medium text-gray-900 dark:text-white">{activity.description}</h5>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              <UserIcon className="w-3 h-3 inline mr-1" />
                              {activity.user?.name || 'Bilinmeyen Kullanıcı'}
                            </p>
                          </div>
                          <div className="text-right">
                            {activity.outcome && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getOutcomeColor(activity.outcome)} mb-1`}>
                                {getOutcomeText(activity.outcome)}
                              </span>
                            )}
                            {activityDate && (
                              <p className="text-sm text-gray-500 dark:text-gray-500">
                                {format(activityDate, 'dd MMM yyyy HH:mm', { locale: tr })}
                              </p>
                            )}
                          </div>
                        </div>
                        {activity.notes && <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">{activity.notes}</p>}

                        {/* Takip Bilgileri */}
                        {activity.follow_up_required && (
                          <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Takip Gerekli</span>
                            </div>
                            {activity.follow_up_date && (
                              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                                <strong>Takip Tarihi:</strong> {format(new Date(activity.follow_up_date), 'dd MMMM yyyy', { locale: tr })}
                              </p>
                            )}
                            {activity.follow_up_notes && (
                              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                                <strong>Takip Notları:</strong> {activity.follow_up_notes}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Metadata (değişiklik detayları) */}
                        {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="text-xs font-medium text-blue-900 dark:text-blue-300 mb-2">Değişiklik Detayları:</p>
                            <div className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                              {activity.metadata.status && (
                                <p><strong>Durum:</strong> {activity.metadata.status.old || 'yok'} → {activity.metadata.status.new || 'yok'}</p>
                              )}
                              {activity.metadata.priority && (
                                <p><strong>Öncelik:</strong> {activity.metadata.priority.old || 'yok'} → {activity.metadata.priority.new || 'yok'}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div >

      {/* Veli Bilgileri */}
      {
        contactRole === 'guardian' && (
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-purple-900 dark:text-purple-300 mb-4">Veli Bilgileri</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem label="Veli Adı" value={getValue(lead, 'first_name')} />
              <InfoItem label="Veli Soyadı" value={getValue(lead, 'last_name')} />
              <EmailsItem label="Veli Email Adresleri" emails={getArrayValue(lead, 'emails')} />
              <InfoItem label="Veli Telefon" value={getValue(lead, 'phone')} />
              <InfoItem label="Veli Uyruğu" value={getValue(lead, 'nationality')} />
              <InfoItem label="Yakınlık Derecesi" value={getRelationshipText(getObjectValue(lead, 'guardian_info')?.relationship)} />
            </div>
          </div>
        )
      }

      {/* Öğrenci / Kişi Bilgileri */}
      <div
        className={`rounded-xl p-6 ${contactRole === 'guardian'
          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
          : 'bg-gray-50 dark:bg-gray-800'
          }`}
      >
        <h4
          className={`text-lg font-semibold mb-4 ${contactRole === 'guardian' ? 'text-blue-900 dark:text-blue-300' : 'text-gray-900 dark:text-white'
            }`}
        >
          {contactRole === 'guardian' ? 'Öğrenci Bilgileri' : 'Öğrenci İletişim Bilgileri'}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {contactRole === 'guardian' ? (
            <>
              <InfoItem
                label="Öğrenci Ad Soyad"
                value={[studentInfoFromLead?.first_name, studentInfoFromLead?.last_name].filter(Boolean).join(' ') || 'Belirtilmemiş'}
              />
              <EmailsItem label="Öğrenci Email Adresleri" emails={getArrayValue(studentInfoFromLead, 'emails')} />
              <InfoItem label="Öğrenci Telefon" value={getValue(studentInfoFromLead, 'phone')} />
              <InfoItem label="Öğrenci Uyruğu" value={getValue(studentInfoFromLead, 'nationality')} />
            </>
          ) : (
            <>
              <InfoItem
                label="Ad Soyad"
                value={[lead?.first_name, lead?.last_name].filter(Boolean).join(' ') || 'Belirtilmemiş'}
              />
              <EmailsItem label="Email Adresleri" emails={getArrayValue(lead, 'emails')} />
              <InfoItem label="Telefon" value={getValue(lead, 'phone')} />
              <InfoItem label="Uyruk" value={getValue(lead, 'nationality')} />
            </>
          )}
        </div>
      </div>

      {/* Vatandaşlık & Oturum */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Vatandaşlık ve Oturum Bilgileri</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoItem label="İtalya Oturum İzni" value={yesNoText(getBooleanValue(lead, 'has_italy_residence_permit'))} />
          <InfoItem label="Başka Ülke Vatandaşlığı" value={yesNoText(getBooleanValue(lead, 'has_other_citizenship'))} />
          <InfoItem label="Diğer Vatandaşlık Ülkesi" value={getValue(lead, 'other_citizenship_country')} />
        </div>
      </div>

      {/* Eğitim Bilgileri */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Eğitim Bilgileri</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoItem
            label="Eğitim Seviyesi"
            value={contactRole === 'guardian' ? getValue(studentInfoFromLead, 'education_level') : getValue(lead, 'education_level')}
          />
          <InfoItem
            label="İngilizce Seviyesi"
            value={contactRole === 'guardian' ? getValue(studentInfoFromLead, 'english_level') : getValue(lead, 'english_level')}
          />
          <InfoItem
            label="İtalyanca Seviyesi"
            value={contactRole === 'guardian' ? getValue(studentInfoFromLead, 'italian_level') : getValue(lead, 'italian_level')}
          />
          <InfoItem label="Akademik Yıl" value={getValue(lead, 'academic_year')} />
          <InfoItem label="Hizmet Yılı" value={getValue(lead, 'service_year')} />
        </div>
      </div>

      {/* İlgi Alanları */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">İlgi Alanları</h4>
        <div className="space-y-4">
          <PillList title="İlgilendiği Hizmetler" items={getArrayValue(lead, 'interested_services')} color="orange" />
          <PillList title="İlgilendiği Programlar" items={interestedPrograms} color="green" />
          <PillList title="İlgilendiği Üniversiteler" items={interestedUniversities} color="purple" />
        </div>
      </div>

      {/* Mali Bilgiler */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Mali Bilgiler</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoItem label="Bütçe" value={getValue(lead, 'budget')} />
          <InfoItem label="Konuşulan Ücret (EUR)" value={getValue(lead, 'discussed_price')} />
          <InfoItem label="İndirim Durumu" value={yesNoText(getBooleanValue(lead, 'has_discount'))} />
          <InfoItem label="İndirim Detayları" value={getValue(lead, 'discount_info')} />
        </div>
        {getValue(lead, 'price_notes') && getValue(lead, 'price_notes') !== 'Belirtilmemiş' && (
          <div className="mt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Ücret Ek Bilgi</p>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-gray-700 dark:text-gray-300">{getValue(lead, 'price_notes')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Takip Durumu */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Takip Durumu</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatusItem label="Ciddi Aday" status={getBooleanValue(lead, 'is_serious_candidate')} />
          <StatusItem label="Vize Bilgisi Verildi" status={getBooleanValue(lead, 'visa_info_provided')} />
          <StatusItem label="Sözleşme Gönderildi" status={getBooleanValue(lead, 'contract_sent')} />
          <StatusItem label="Form Tamamlandı" status={getBooleanValue(lead, 'form_completed')} />
          <StatusItem label="Email Gönderildi" status={getBooleanValue(lead, 'email_sent')} />
          <StatusItem
            label="Kayıt Oldu"
            status={getBooleanValue(lead, 'is_registered')}
            clickable={true}
            onClick={handleEnrollmentToggle}
          />
        </div>
      </div>

      {/* Etkileşimde Bulunan Çalışanlar */}
      {
        (() => {
          // Unique çalışanları topla
          const uniqueStaff = new Map();

          // Lead oluşturan kişi
          if (lead.created_by) {
            if (lead.creator) {
              uniqueStaff.set(lead.creator.id, {
                id: lead.creator.id,
                name: lead.creator.name,
                email: lead.creator.email,
                timestamp: lead.created_at,
                action: 'Lead Oluşturdu'
              });
            }
          } else {
            uniqueStaff.set('system', {
              id: 'system',
              name: 'Sistemden Yüklendi',
              email: '',
              timestamp: lead.created_at,
              action: 'Lead Oluşturdu'
            });
          }

          // Lead güncelleyen kişi (eğer oluşturandan farklıysa)
          if (lead.updated_by && lead.updated_by !== lead.created_by) {
            // updated_by için profil bilgisini çek (eğer varsa)
            const updaterName = lead.updater?.name || 'Bilinmeyen Kullanıcı';
            uniqueStaff.set(lead.updated_by, {
              id: lead.updated_by,
              name: updaterName,
              email: lead.updater?.email || '',
              timestamp: lead.updated_at,
              action: 'Son Güncelleme'
            });
          }

          // Atanan danışmanlar (eğer yukarıdakilerden farklıysa)
          if (lead.consultants_info) {
            lead.consultants_info.forEach((consultant: any) => {
              if (!uniqueStaff.has(consultant.id)) {
                uniqueStaff.set(consultant.id, {
                  id: consultant.id,
                  name: consultant.name,
                  email: consultant.email,
                  timestamp: lead.created_at,
                  action: 'Atandı'
                });
              }
            });
          }

          const staffArray = Array.from(uniqueStaff.values());

          if (staffArray.length === 0) return null;

          return (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-300">Etkileşimde Bulunan Çalışanlar</h4>
              </div>
              <div className="space-y-3">
                {staffArray.map((staff) => (
                  <div
                    key={staff.id}
                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-200 dark:border-blue-800"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{staff.name || 'İsimsiz Çalışan'}</p>
                        {staff.email && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">{staff.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium text-blue-600 dark:text-blue-400">{staff.action}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">{formatDateTime(staff.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()
      }

      {/* Atanan Danışmanlar (Eski versiyon - fallback) */}
      {
        (!lead.consultants_info || lead.consultants_info.length === 0) && assignedConsultants.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Atanan Danışmanlar</h4>
            <div className="flex flex-wrap gap-2">
              {assignedConsultants.map((consultant: any, index: number) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 text-sm rounded-full"
                >
                  {typeof consultant === 'string' ? consultant : consultant?.name || `Danışman ${index + 1}`}
                </span>
              ))}
            </div>
          </div>
        )
      }

      {/* Sesli Notlar */}
      {
        voiceNotes.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Sesli Notlar ({voiceNotes.length})
            </h4>
            <div className="space-y-3">
              {voiceNotes.map((note: any, index: number) => {
                const noteId = note?.id || index.toString();
                return (
                  <div
                    key={noteId}
                    className="flex items-center justify-between p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => console.log('Playing voice note:', note)}
                        className="p-3 rounded-full bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 transition-colors"
                      >
                        <Play className="w-5 h-5" />
                      </button>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Sesli Not {index + 1}</p>
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <Volume2 className="w-4 h-4" />
                          <span>{note?.duration || 30}s</span>
                          <span>•</span>
                          <span>{formatDateTime(note?.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => console.log('Download voice note:', note)}
                      className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                      title="Sesli Notu İndir"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )
      }

      {/* Etiketler */}
      {
        tags.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Etiketler</h4>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag: string, index: number) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 text-sm rounded-full"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )
      }

      {/* Notlar */}
      {
        getValue(lead, 'notes') && getValue(lead, 'notes') !== 'Belirtilmemiş' && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notlar</h4>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-gray-700 dark:text-gray-300">{getValue(lead, 'notes')}</p>
            </div>
          </div>
        )
      }

    </div >
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <span className="text-white text-lg font-semibold">
                {`${(lead?.first_name || '').toString().charAt(0)}${(lead?.last_name || '').toString().charAt(0)}`}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {[lead?.first_name, lead?.last_name].filter(Boolean).join(' ') || 'İsimsiz'}
              </h2>
              <div className="flex items-center space-x-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(getValue(lead, 'status'))}`}>
                  {getStatusText(getValue(lead, 'status'))}
                </span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${contactRole === 'guardian'
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                    }`}
                >
                  {contactRole === 'guardian' ? 'Veli' : 'Öğrenci'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={onEdit} className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
              <Edit className="w-4 h-4" />
              <span>Düzenle</span>
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderDetails()}
        </div>
      </div>
    </div>
  );
};

export default LeadDetailModal;
